import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'

import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'

import Stethoscope, { StethoscopeProps, StethoscopeHandle } from '@/components/group/Stethoscope'
import AudioRecordingTable, { AudioRecordingTableProps } from '@/components/ui/tables/AudioRecordingTable'

import { useDiagnosticRTC } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'

import DiagnosticRTCServiceSwitch from './DiagnosticRTCServiceSwitch'

export type DiagnosticStethoscopeProps = Omit<
  StethoscopeProps,
  'audioInputMediaDevices' | 'isStreamer' | 'renderAudio' | 'renderVisualizer' | 'stethoscopes' | 'stream'
> & {
  audioRecordings?: AudioRecordingTableProps['data']
  externalAudioInputActive?: boolean
  externalSelectedDeviceId?: string
  onDeleteAnnotation?: AudioRecordingTableProps['onDeleteAnnotation']
  onUpdateAnnotation?: AudioRecordingTableProps['onUpdateAnnotation']
}

export const DiagnosticStethoscope = forwardRef<StethoscopeHandle, DiagnosticStethoscopeProps>(function DiagnosticStethoscope(props, ref) {
  const { audioRecordings, externalAudioInputActive, externalSelectedDeviceId, onDeleteAnnotation, onUpdateAnnotation, ...rest } = props
  const {
    activeDiagnosticRTCService,
    changeDiagnosticRTCService,
    devices,
    diagnosticRTCServices,
    isStreamer,
    renderAudio,
    renderAudioVisualizer,
    selectDevice,
    stopMicro,
    stream,
  } = useDiagnosticRTC()
  const { t } = useTranslation()

  return (
    <Container maxWidth="md" sx={{ pt: '20px', pb: '20px' }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="div" sx={{ fontSize: '18px', fontWeight: 500 }}>
            {t('page.liveConsultation.stethoscope.title')}
          </Typography>
        </Stack>
        <Stack alignItems="end">
          <DiagnosticRTCServiceSwitch onChange={changeDiagnosticRTCService} selectedService={activeDiagnosticRTCService} services={diagnosticRTCServices} />
          <Stethoscope
            {...rest}
            ref={ref}
            externalAudioInputActive={externalAudioInputActive}
            externalSelectedDeviceId={externalSelectedDeviceId}
            audioInputMediaDevices={devices.audioInputMediaDevices}
            isStreamer={isStreamer}
            onSelectDevice={selectDevice}
            onStopMicro={() => { stopMicro(); rest.onStopMicro?.() }}
            renderAudio={renderAudio}
            renderVisualizer={renderAudioVisualizer}
            stethoscopes={devices.stethoscopes}
            stream={stream}
          />
        </Stack>
        <AudioRecordingTable data={audioRecordings} onDeleteAnnotation={onDeleteAnnotation} onUpdateAnnotation={onUpdateAnnotation} />
      </Stack>
    </Container>
  )
})

export default DiagnosticStethoscope
